import React, { useState, useEffect } from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBTable,
  MDBTableBody,
  MDBSpinner,
} from "mdb-react-ui-kit";
import { format } from "date-fns";
import printFloorAgendaPDF from "../../utils/printFloorAgendaPDF";
import fetchSponsorsDetails from "../../utils/fetchSponsorDetails";
import fetchBillVotesDetails from "../../utils/fetchBillVotesDetails";
import fetchBillDetailsWithBillId from "../../utils/fetchBillDetailsWithBillId";

const AgendaModal = ({
  showModal,
  setShowModal,
  selectedAgenda,
  committeeData,
}) => {
  const [enhancedAgenda, setEnhancedAgenda] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only fetch bill details when the modal is shown and we have a selected agenda
    const fetchBillDetails = async () => {
      if (showModal && selectedAgenda && selectedAgenda.agendaBills) {
        setLoading(true);
        try {
          // Create a copy of the selected agenda to enhance
          const agendaCopy = { ...selectedAgenda };

          // Enhance agenda bills with additional details
          if (agendaCopy.agendaBills && agendaCopy.agendaBills.length > 0) {
            agendaCopy.agendaBills = await Promise.all(
              agendaCopy.agendaBills.map(async (bill) => {
                try {
                  const sponsor = await fetchSponsorsDetails(bill.legislatorId);
                  const billVoteDetails = await fetchBillVotesDetails(
                    bill.billId
                  );
                  const billDetails = await fetchBillDetailsWithBillId(
                    bill.billId,
                    committeeData.sessionId
                  );

                  if (billDetails.carrierId) {
                    const carrier = await fetchSponsorsDetails(billDetails.carrierId);
                    billDetails.carrierInfo = {
                      firstName: carrier.firstName,
                      lastName: carrier.lastName
                    };
                  }

                  return {
                    ...bill,
                    sponsor,
                    billVoteDetails,
                    billDetails,
                  };
                } catch (error) {
                  console.error("Error fetching bill details:", error);
                  return bill;
                }
              })
            );
          }

          setEnhancedAgenda(agendaCopy);
        } catch (error) {
          console.error("Error enhancing agenda:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBillDetails();
  }, [showModal, selectedAgenda, committeeData]);

  const toggleOpen = () => setShowModal(!showModal);

  // Use the enhanced agenda if available, otherwise fall back to the selected agenda
  const displayAgenda = enhancedAgenda || selectedAgenda;

  const processVotes = (billVoteDetails, committeeData) => {
    let totalYesVotes = 0;
    let totalNoVotes = 0;
    let committeeName = "Not available";
    let latestVoteDate = new Date(0); // Initialize to the earliest possible date

    const getLatestConferenceVote = (executiveActionData) => {
      let latestVote = null;

      executiveActionData.forEach(voteDetail => {
        const committeeTypeDescription = voteDetail.standingCommitteeMeeting?.standingCommittee?.committeeDetails?.committeeCode?.committeeType?.description;

        if (committeeTypeDescription && committeeTypeDescription.toLowerCase().includes("conference")) {
          const voteTime = new Date(voteDetail.voteTime);

          if (!latestVote || voteTime > new Date(latestVote.voteTime)) {
            latestVote = voteDetail;
          }
        }
      });

      return latestVote;
    };

    if (billVoteDetails && billVoteDetails.executiveActionData) {
      const latestConferenceVote = getLatestConferenceVote(billVoteDetails.executiveActionData);
      // Check if we have a conference vote, If we have conference vote then we should give it priprity without matchin the chamber. 
      if (latestConferenceVote) {
        totalYesVotes = latestConferenceVote.legislatorVotes.filter(
          (vote) => vote.committeeVote === "YES" || vote.committeeVote === "YES_BY_PROXY"
        ).length;

        totalNoVotes = latestConferenceVote.legislatorVotes.filter(
          (vote) => vote.committeeVote === "NO" || vote.committeeVote === "NO_BY_PROXY"
        ).length;

        committeeName = latestConferenceVote.standingCommitteeMeeting.standingCommittee.committeeDetails?.committeeCode?.name || "Not available";
      } else {
        // If there is no conference vote then we should match the chamber of the committeeData
        for (let i = billVoteDetails.executiveActionData.length - 1; i >= 0; i--) {
          const voteDetail = billVoteDetails.executiveActionData[i];
          const voteDate = new Date(voteDetail.voteTime);

          if (voteDate > latestVoteDate && voteDetail.standingCommitteeMeeting?.standingCommittee?.chamber === committeeData.chamber) {
            latestVoteDate = voteDate;
            totalYesVotes = voteDetail.legislatorVotes.filter(
              (vote) => vote.committeeVote === "YES" || vote.committeeVote === "YES_BY_PROXY"
            ).length;

            totalNoVotes = voteDetail.legislatorVotes.filter(
              (vote) => vote.committeeVote === "NO" || vote.committeeVote === "NO_BY_PROXY"
            ).length;

            committeeName = voteDetail.standingCommitteeMeeting.standingCommittee.committeeDetails?.committeeCode?.name || "Not available";
          }
        }
      }
    }

    return `${committeeName}: Vote: ${totalYesVotes}-${totalNoVotes}`;
  };

  // Show loading spinner while fetching bill details
  if (loading) {
    return (
      <MDBModal open={showModal} tabIndex="-1">
        <MDBModalDialog size="xl">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Loading Agenda Details</MDBModalTitle>
              <MDBBtn
                className="btn-close"
                color="none"
                onClick={toggleOpen}
              ></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody className="text-center p-5">
              <MDBSpinner role="status">
                <span className="visually-hidden">Loading...</span>
              </MDBSpinner>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }

  // Don't render anything if no agenda is selected
  if (!displayAgenda) {
    return null;
  }

  return (
    <MDBModal
      open={showModal}
      tabIndex="-1"
      onClose={() => setShowModal(false)}
    >
      <MDBModalDialog size="xl" scrollable>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle className="w-100">Agenda</MDBModalTitle>
            <MDBBtn
              className="text-nowrap me-3"
              onClick={() => printFloorAgendaPDF(displayAgenda)}
            >
              <MDBIcon fas icon="print" /> Print
            </MDBBtn>
            <MDBBtn
              className="btn-close"
              color="none"
              onClick={toggleOpen}
            ></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            <div
              className={`w-100 text-center text-white my-3 ${
                displayAgenda?.chamber === "SENATE"
                  ? "bg-color6"
                  : displayAgenda?.chamber === "HOUSE"
                  ? "bg-color3"
                  : "bg-color4"
              }`}
            >
              2025 Montana Legislature - 69th Regular Session
            </div>
            <div className="agenda-header text-center mb-3">
              <h3>
                {" "}
                {displayAgenda?.chamber &&
                  displayAgenda.chamber
                    .toLowerCase()
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}{" "}
                Agenda #{displayAgenda?.agendaNumber}
              </h3>
              <div>
                Legislative Day{" "}
                {displayAgenda?.legislativeCalendarDay?.calendarDay}
              </div>
              <div>
                {displayAgenda?.formattedAgendaDate &&
                  `${new Date(
                    displayAgenda.formattedAgendaDate
                  ).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}, ${
                    displayAgenda?.conveneTime
                      ? format(new Date(displayAgenda.conveneTime), "h:mm a")
                      : ""
                  }`}
              </div>
            </div>
            <div className="agenda-content">
              {displayAgenda?.agendaBills &&
                Object.entries(
                  displayAgenda.agendaBills.reduce((groups, item) => {
                    const description =
                      item.orderOfBusiness?.description || "Other";
                    if (!groups[description]) {
                      groups[description] = [];
                    }
                    groups[description].push(item);
                    return groups;
                  }, {})
                )
                  .sort(([descA], [descB]) => {
                    const order = {
                      "First Reading of Bills": 1,
                      "Second Reading of Bills": 2,
                      "Third Reading of Bills": 3,
                      Other: 4,
                    };
                    return (order[descA] || 999) - (order[descB] || 999);
                  })
                  .map(([description, bills]) => {
                    // Sort the bills by billOrderNumber within each group
                    const sortedBills = bills.sort(
                      (a, b) => a.billOrderNumber - b.billOrderNumber
                    );
                    return (
                      <div key={description}>
                        <strong className="mb-3">
                          Order of Business #
                          {sortedBills[0]?.orderOfBusiness?.order} --{" "}
                          {description}
                        </strong>
                        <MDBTable small className="mb-0">
                          <MDBTableBody>
                            {sortedBills.map((bill, index) => (
                              <tr key={bill.id}>
                                <td width="3%">{index + 1})</td>
                                <td width="7%">
                                  <strong>
                                    <a
                                      className="text-decoration-underline"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      href={`${process.env.REACT_APP_BILLS_EXPLORER}/#/laws/bill/${committeeData.sessionId}/${bill?.billDetails?.draft.draftNumber}`}
                                    >
                                      {bill?.billDetails?.billType?.code}{" "}
                                      {bill?.billDetails?.billNumber}
                                    </a>
                                  </strong>
                                </td>
                                <td width="10%">
                                  <small>Sponsor:</small>{" "}
                                  <div>
                                    {bill?.sponsor?.lastName},{" "}
                                    {bill?.sponsor?.firstName?.charAt(0)}
                                  </div>
                                </td>
                                <td width="10%">
                                {bill?.billDetails?.carrierId &&
                                  bill?.billDetails?.carrierInfo &&
                                  ((displayAgenda.chamber === "SENATE" &&
                                    ["HB", "HJ", "HR"].includes(bill?.billDetails?.billType?.code)) ||
                                  (displayAgenda.chamber === "HOUSE" &&
                                    ["SB", "SJ", "SR"].includes(bill?.billDetails?.billType?.code))) ? (
                                    <>
                                      <small>Carrier:</small>
                                      <div>
                                        {bill.billDetails.carrierInfo.lastName},{" "}
                                        {bill.billDetails.carrierInfo.firstName.charAt(0)}
                                      </div>
                                    </>
                                  ) : null}
                                </td>
                                <td width="20%">
                                  <small>
                                    {bill.comment
                                      ? bill.comment.replace(/<\/?p>/g, "")
                                      : null}
                                  </small>
                                </td>
                                <td width="50%">
                                  <div>
                                    {bill.billDetails &&
                                    bill?.billDetails?.draft?.shortTitle
                                      ? bill.billDetails.draft.shortTitle
                                      : "N/A"}
                                  </div>
                                  <small className="ms-4">
                                    {processVotes(bill.billVoteDetails, committeeData)}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </MDBTableBody>
                        </MDBTable>
                      </div>
                    );
                  })}
            </div>
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="dark" onClick={toggleOpen}>
              Close
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
};

export default AgendaModal;
